import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Scenario } from "../types";
import { getScenario } from "../utils/apiUtil";
import {
  createScenarioDraft,
  updateScenarioDraft,
  publishScenario,
  deleteScenarioDraft,
  listCategories,
  createPersona,
  updatePersona,
  deletePersona,
  createDocument,
  deleteDocument,
  uploadAsset,
} from "../services/scenarioAdminService";
import {
  buildScenarioArchive,
  downloadBlob,
  parseScenarioArchive,
  scenarioArchiveFilename,
} from "../utils/scenarioArchive";
import {
  buildPartialImportSaveMessage,
  replaceSavedDocumentDraft,
  replaceSavedPersonaDraft,
} from "../utils/scenarioDraftIdentity";
import CategoryCombobox, { CategoryValue } from "./CategoryCombobox";
import { categoryPatch } from "../utils/categoryPatch";
import PersonaFormModal, { PersonaDraft } from "./PersonaFormModal";
import PersonaAvatar from "./PersonaAvatar";
import DocumentFormModal, { DocumentDraft } from "./DocumentFormModal";
import { useObjectUrls } from "../hooks/useObjectUrls";
import { usePermissionLevel } from "../hooks/usePermissionLevel";

interface ScenarioCreatePageProps {
  // null = creating; non-null = editing an existing draft
  scenarioId: string | null;
  getToken: () => string | undefined;
  onExitToLibrary: () => void;
}

const ScenarioCreatePage: React.FC<ScenarioCreatePageProps> = ({
  scenarioId: scenarioIdProp,
  getToken,
  onExitToLibrary,
}) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { isAdmin } = usePermissionLevel();

  const [scenarioId, setScenarioId] = useState<string | null>(scenarioIdProp);
  const [scenarioStatus, setScenarioStatus] = useState<
    Scenario["status"]
  >("draft");
  const [isGlobal, setIsGlobal] = useState(false);
  const [bootstrapping, setBootstrapping] = useState<boolean>(!scenarioIdProp);
  const [loadingExisting, setLoadingExisting] = useState<boolean>(
    !!scenarioIdProp,
  );
  const [error, setError] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);

  // Form state
  const [title, setTitle] = useState<string>("");
  const [categoryValue, setCategoryValue] = useState<CategoryValue>(null);
  const [description, setDescription] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const [playerRole, setPlayerRole] = useState<string>("");
  const [objective, setObjective] = useState<string>("");
  const [briefing, setBriefing] = useState<string>("");
  const [logoObjectKey, setLogoObjectKey] = useState<string | undefined>(
    undefined,
  );
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>(
    undefined,
  );
  const [importedLogoFile, setImportedLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState<boolean>(false);
  const [heroObjectKey, setHeroObjectKey] = useState<string | undefined>(
    undefined,
  );
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | undefined>(
    undefined,
  );
  const [importedHeroFile, setImportedHeroFile] = useState<File | null>(null);
  const [heroUploading, setHeroUploading] = useState<boolean>(false);
  const [importingArchive, setImportingArchive] = useState<boolean>(false);
  const [exportingArchive, setExportingArchive] = useState<boolean>(false);

  const [personas, setPersonas] = useState<PersonaDraft[]>([]);
  const [documents, setDocuments] = useState<DocumentDraft[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  );

  const { create: createBlobUrl, revoke: revokeBlobUrl } = useObjectUrls();

  const [personaModalOpen, setPersonaModalOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<PersonaDraft | null>(
    null,
  );
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DocumentDraft | null>(
    null,
  );

  // ---- Bootstrap: create new draft or hydrate existing ----
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const token = getToken();
      try {
        if (scenarioIdProp) {
          setLoadingExisting(true);
          const sc: Scenario = await getScenario(scenarioIdProp, token);
          if (cancelled) return;
          setScenarioId(sc.id);
          setScenarioStatus(sc.status);
          setIsGlobal(Boolean(sc.isGlobal));
          setTitle(sc.title);
          setDescription(sc.description);
          setInstructions(sc.contextPrompt);
          setPlayerRole(sc.playerRole ?? "");
          setObjective(sc.objective ?? "");
          setBriefing(sc.briefing ?? "");
          // Logo/hero are signed URLs; the underlying objectKeys aren't
          // returned. For now show the URLs as previews only.
          setLogoPreviewUrl(sc.logo);
          setHeroPreviewUrl(sc.hero);
          setCategoryValue(
            sc.category
              ? {
                  kind: "existing",
                  id: "",
                  name: sc.category,
                }
              : null,
          );
          setPersonas(
            sc.personas.map((p) => ({
              id: p.id,
              name: p.name,
              role: p.role,
              description: p.description,
              bio: p.bio ?? "",
              voiceId: p.voiceId ?? "",
              instructions: p.instructions,
              avatarPreviewUrl: p.avatar || undefined,
              avatarSourceUrl: p.avatar || undefined,
              accessibleDocumentIds: p.accessibleDocumentIds,
            })),
          );
          setDocuments(
            sc.documents.map((d) => ({
              id: d.id,
              name: d.name,
              description: d.description,
              instructions: d.instructions,
              objectKey: undefined, // server doesn't expose it
              sourceUrl: d.url,
            })),
          );
          setLoadingExisting(false);
        } else {
          // Create-on-mount so the draft is resumable and uploads have a
          // scenarioId to scope against.
          setBootstrapping(true);
          const { id } = await createScenarioDraft(token);
          if (cancelled) return;
          setScenarioId(id);
          setScenarioStatus("draft");
          // Move the URL to the resumable edit route. Hash-replace so the
          // back button skips the creating-state.
          navigate(`/${orgSlug}/scenarios/${id}/edit`, { replace: true });
          setBootstrapping(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Failed to load create page.",
          );
          setBootstrapping(false);
          setLoadingExisting(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // We only want to run this once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSaveDraft = scenarioStatus === "draft";

  // ---- Category list (separate fetch, non-blocking) ----
  useEffect(() => {
    const run = async () => {
      try {
        const cats = await listCategories(getToken());
        setCategories(cats);
        // Reconcile a hydrated category-by-name into an id-bearing value
        // once the list comes in.
        setCategoryValue((prev) => {
          if (prev && prev.kind === "existing" && !prev.id) {
            const match = cats.find((c) => c.name === prev.name);
            return match
              ? { kind: "existing", id: match.id, name: match.name }
              : prev;
          }
          return prev;
        });
      } catch (e) {
        console.error("Failed to load categories", e);
      }
    };
    run();
  }, [getToken]);

  // ---- Validation (mirrors server) ----
  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!title.trim()) errors.push("Title is required.");
    if (!categoryValue) errors.push("Category is required.");
    if (!description.trim()) errors.push("Description is required.");
    if (!instructions.trim()) errors.push("Instructions are required.");
    if (personas.length === 0)
      errors.push("At least one persona is required.");
    return errors;
  }, [
    title,
    categoryValue,
    description,
    instructions,
    personas.length,
  ]);

  const persistBasics = useCallback(
    async (overrides?: {
      logoObjectKey?: string;
      heroObjectKey?: string;
    }) => {
      if (!scenarioId) return;
      const token = getToken();
      const patch: Parameters<typeof updateScenarioDraft>[1] = {
        title,
        description,
        instructions,
        playerRole,
        objective,
        briefing,
      };
      if (overrides && "logoObjectKey" in overrides) {
        patch.logoObjectKey = overrides.logoObjectKey;
      } else if (logoObjectKey !== undefined) {
        patch.logoObjectKey = logoObjectKey;
      }
      if (overrides && "heroObjectKey" in overrides) {
        patch.heroObjectKey = overrides.heroObjectKey;
      } else if (heroObjectKey !== undefined) {
        patch.heroObjectKey = heroObjectKey;
      }
      Object.assign(patch, categoryPatch(categoryValue));
      // Only an admin may set this — sending it as a manager earns a 403.
      // isAdmin already gates the toggle's visibility, but re-check here so a
      // stale isGlobal value can never sneak into a manager's save payload.
      if (isAdmin) {
        patch.isGlobal = isGlobal;
      }
      await updateScenarioDraft(scenarioId, patch, token);
    },
    [
      scenarioId,
      title,
      description,
      instructions,
      playerRole,
      objective,
      briefing,
      logoObjectKey,
      heroObjectKey,
      categoryValue,
      isAdmin,
      isGlobal,
      getToken,
    ],
  );

  const persistImportedItems = useCallback(async () => {
    if (!scenarioId) return;
    const token = getToken();
    let persistedImportedItems = 0;
    let resolvedLogoObjectKey = logoObjectKey;
    let resolvedHeroObjectKey = heroObjectKey;
    try {
      if (importedLogoFile) {
        resolvedLogoObjectKey = await uploadAsset(
          "scenario-logo",
          { scenarioId },
          importedLogoFile,
          token,
        );
        setLogoObjectKey(resolvedLogoObjectKey);
        setImportedLogoFile(null);
      }

      if (importedHeroFile) {
        resolvedHeroObjectKey = await uploadAsset(
          "scenario-hero",
          { scenarioId },
          importedHeroFile,
          token,
        );
        setHeroObjectKey(resolvedHeroObjectKey);
        setImportedHeroFile(null);
      }

      await persistBasics({
        logoObjectKey: resolvedLogoObjectKey,
        heroObjectKey: resolvedHeroObjectKey,
      });

      const documentIdByObjectKey = new Map<string, string>();
      for (const doc of documents) {
        if (doc.id) {
          if (doc.objectKey) documentIdByObjectKey.set(doc.objectKey, doc.id);
          if (doc.importedObjectKey) {
            documentIdByObjectKey.set(doc.importedObjectKey, doc.id);
          }
          continue;
        }
        if (!doc.importedFile) {
          throw new Error(
            `Imported document "${doc.name}" is missing its file in the zip.`,
          );
        }
        const objectKey = await uploadAsset(
          "document",
          { scenarioId },
          doc.importedFile,
          token,
        );
        const created = await createDocument(
          scenarioId,
          {
            name: doc.name.trim(),
            description: doc.description.trim(),
            instructions: doc.instructions?.trim() || undefined,
            objectKey,
          },
          token,
        );
        const savedDocument = {
          ...doc,
          id: created.id,
          importedObjectKey: doc.importedObjectKey ?? doc.objectKey,
          objectKey,
          importedFile: undefined,
          stagedFilename: undefined,
        };
        persistedImportedItems += 1;
        if (doc.objectKey) documentIdByObjectKey.set(doc.objectKey, created.id);
        documentIdByObjectKey.set(objectKey, created.id);
        setDocuments((prev) => replaceSavedDocumentDraft(prev, savedDocument));
      }

      for (const persona of personas) {
        const accessibleDocumentIds = [
          ...persona.accessibleDocumentIds,
          ...(persona.accessibleDocumentKeys ?? [])
            .map((key) => documentIdByObjectKey.get(key))
            .filter((id): id is string => !!id),
        ];
        const uniqueAccessibleDocumentIds = [...new Set(accessibleDocumentIds)];
        let savedPersona: PersonaDraft;
        if (persona.id) {
          savedPersona = {
            ...persona,
            accessibleDocumentIds: uniqueAccessibleDocumentIds,
            accessibleDocumentKeys: undefined,
          };
        } else {
          const created = await createPersona(
            scenarioId,
            {
              name: persona.name.trim(),
              role: persona.role.trim(),
              description: persona.description.trim() || undefined,
              bio: persona.bio.trim() || undefined,
              voiceId: persona.voiceId.trim() || null,
              instructions: persona.instructions,
              accessibleDocumentIds: uniqueAccessibleDocumentIds,
              avatarObjectKey: persona.avatarObjectKey,
            },
            token,
          );
          savedPersona = {
            ...persona,
            id: created.id,
            accessibleDocumentIds: uniqueAccessibleDocumentIds,
            accessibleDocumentKeys: undefined,
          };
          persistedImportedItems += 1;
          setPersonas((prev) => replaceSavedPersonaDraft(prev, savedPersona));
        }

        if (savedPersona.avatarFile) {
          if (!savedPersona.id) {
            throw new Error(
              `Imported persona "${savedPersona.name}" is missing its server id.`,
            );
          }
          const avatarObjectKey = await uploadAsset(
            "persona-avatar",
            { scenarioId, personaId: savedPersona.id },
            savedPersona.avatarFile,
            token,
          );
          await updatePersona(
            scenarioId,
            savedPersona.id,
            { avatarObjectKey },
            token,
          );
          savedPersona = {
            ...savedPersona,
            avatarObjectKey,
            avatarFile: undefined,
          };
        } else if (persona.id && persona.accessibleDocumentKeys?.length) {
          await updatePersona(
            scenarioId,
            persona.id,
            { accessibleDocumentIds: uniqueAccessibleDocumentIds },
            token,
          );
        }

        setPersonas((prev) => replaceSavedPersonaDraft(prev, savedPersona));
      }
    } catch (error) {
      if (persistedImportedItems > 0) {
        const message =
          error instanceof Error ? error.message : "Failed to save import.";
        throw new Error(buildPartialImportSaveMessage(message));
      }
      throw error;
    }
  }, [
    scenarioId,
    getToken,
    logoObjectKey,
    importedLogoFile,
    heroObjectKey,
    importedHeroFile,
    persistBasics,
    documents,
    personas,
  ]);

  const handleSaveDraft = async () => {
    if (!scenarioId) return;
    setSavingDraft(true);
    setError(null);
    try {
      await persistImportedItems();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!scenarioId) return;
    if (validationErrors.length > 0) {
      setError(validationErrors.join(" "));
      return;
    }
    setPublishing(true);
    setError(null);
    try {
      // Flush latest basics first.
      await persistImportedItems();
      await publishScenario(scenarioId, getToken());
      onExitToLibrary();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to publish.");
    } finally {
      setPublishing(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!scenarioId) return;
    if (!window.confirm("Discard this draft? This cannot be undone.")) return;
    try {
      await deleteScenarioDraft(scenarioId, getToken());
      onExitToLibrary();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to discard draft.");
    }
  };

  const handleLogoSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !scenarioId) return;
    setLogoUploading(true);
    setError(null);
    try {
      const objectKey = await uploadAsset(
        "scenario-logo",
        { scenarioId },
        file,
        getToken(),
      );
      setLogoObjectKey(objectKey);
      setImportedLogoFile(null);
      revokeBlobUrl(logoPreviewUrl);
      setLogoPreviewUrl(createBlobUrl(file));
      // Persist immediately so the draft remembers the logo even if the user
      // navigates away before pressing Save Draft.
      await persistBasics({ logoObjectKey: objectKey });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleHeroSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !scenarioId) return;
    setHeroUploading(true);
    setError(null);
    try {
      const objectKey = await uploadAsset(
        "scenario-hero",
        { scenarioId },
        file,
        getToken(),
      );
      setHeroObjectKey(objectKey);
      revokeBlobUrl(heroPreviewUrl);
      setHeroPreviewUrl(createBlobUrl(file));
      // Persist immediately so the draft remembers the hero image even if
      // the user navigates away before pressing Save Draft.
      await persistBasics({ heroObjectKey: objectKey });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload hero image.");
    } finally {
      setHeroUploading(false);
    }
  };

  const handleImportArchive = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const persistedPersonas = personas.filter(
      (p): p is PersonaDraft & { id: string } => !!p.id,
    );
    const persistedDocuments = documents.filter(
      (d): d is DocumentDraft & { id: string } => !!d.id,
    );
    const hasPersistedChildren =
      persistedPersonas.length + persistedDocuments.length > 0;

    if (scenarioId && hasPersistedChildren) {
      const parts: string[] = [];
      if (persistedPersonas.length) {
        parts.push(
          `${persistedPersonas.length} persona${persistedPersonas.length === 1 ? "" : "s"}`,
        );
      }
      if (persistedDocuments.length) {
        parts.push(
          `${persistedDocuments.length} document${persistedDocuments.length === 1 ? "" : "s"}`,
        );
      }
      const confirmed = window.confirm(
        `Importing this archive will delete ${parts.join(" and ")} already saved on this draft. Continue?`,
      );
      if (!confirmed) return;
    }

    setImportingArchive(true);
    setError(null);
    try {
      const imported = await parseScenarioArchive(file);
      if (scenarioId && hasPersistedChildren) {
        const token = getToken();
        await Promise.all([
          ...persistedPersonas.map((p) =>
            deletePersona(scenarioId, p.id, token),
          ),
          ...persistedDocuments.map((d) =>
            deleteDocument(scenarioId, d.id, token),
          ),
        ]);
      }
      setTitle(imported.title);
      const matchingCategory = categories.find(
        (category) =>
          category.name.toLowerCase() === imported.categoryName.toLowerCase(),
      );
      setCategoryValue(
        matchingCategory
          ? {
              kind: "existing",
              id: matchingCategory.id,
              name: matchingCategory.name,
            }
          : imported.categoryName
            ? {
                kind: "new",
                name: imported.categoryName,
              }
            : null,
      );
      setDescription(imported.description);
      setInstructions(imported.instructions);
      setPlayerRole(imported.playerRole);
      setObjective(imported.objective);
      setBriefing(imported.briefing);
      setLogoObjectKey(imported.logoObjectKey);
      setImportedLogoFile(imported.logoFile ?? null);
      revokeBlobUrl(logoPreviewUrl);
      setLogoPreviewUrl(
        imported.logoFile ? createBlobUrl(imported.logoFile) : undefined,
      );
      setHeroObjectKey(imported.heroObjectKey);
      setImportedHeroFile(imported.heroFile ?? null);
      revokeBlobUrl(heroPreviewUrl);
      setHeroPreviewUrl(
        imported.heroFile ? createBlobUrl(imported.heroFile) : undefined,
      );
      setDocuments(imported.documents);
      personas.forEach((p) => revokeBlobUrl(p.avatarPreviewUrl));
      setPersonas(
        imported.personas.map((p) => ({
          ...p,
          bio: p.bio ?? "",
          voiceId: p.voiceId ?? "",
          avatarPreviewUrl: p.avatarFile ? createBlobUrl(p.avatarFile) : undefined,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to import scenario.");
    } finally {
      setImportingArchive(false);
    }
  };

  const handleExportArchive = async () => {
    setExportingArchive(true);
    setError(null);
    try {
      const documentKeyById = new Map(
        documents
          .filter((doc) => doc.id && doc.objectKey)
          .map((doc) => [doc.id as string, doc.objectKey as string]),
      );
      const blob = await buildScenarioArchive({
        slug: title,
        title,
        category: categoryValue?.name ?? "",
        description,
        instructions,
        playerRole,
        objective,
        briefing,
        logoObjectKey,
        logoSourceUrl: logoPreviewUrl,
        logoFile: importedLogoFile,
        heroObjectKey,
        heroSourceUrl: heroPreviewUrl,
        heroFile: importedHeroFile,
        archiveAssetToken: getToken(),
        documents,
        personas: personas.map((persona) => ({
          ...persona,
          accessibleDocumentKeys: [
            ...(persona.accessibleDocumentKeys ?? []),
            ...persona.accessibleDocumentIds
              .map((id) => documentKeyById.get(id))
              .filter((key): key is string => !!key),
          ],
        })),
      });
      downloadBlob(blob, scenarioArchiveFilename(title));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to export scenario.");
    } finally {
      setExportingArchive(false);
    }
  };

  // ---- Persona handlers ----
  const handleAddPersona = () => {
    setEditingPersona(null);
    setPersonaModalOpen(true);
  };

  const handleEditPersona = (p: PersonaDraft) => {
    setEditingPersona(p);
    setPersonaModalOpen(true);
  };

  const handlePersonaSaved = (p: PersonaDraft) => {
    setPersonas((prev) => replaceSavedPersonaDraft(prev, p));
    setPersonaModalOpen(false);
  };

  const handleRemovePersona = async (p: PersonaDraft) => {
    if (!scenarioId || !p.id) return;
    if (!window.confirm(`Remove persona "${p.name}"?`)) return;
    try {
      await deletePersona(scenarioId, p.id, getToken());
      setPersonas((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove persona.");
    }
  };

  // ---- Document handlers ----
  const handleAddDocument = () => {
    setEditingDocument(null);
    setDocumentModalOpen(true);
  };

  const handleEditDocument = (d: DocumentDraft) => {
    setEditingDocument(d);
    setDocumentModalOpen(true);
  };

  const handleDocumentSaved = (d: DocumentDraft) => {
    setDocuments((prev) => replaceSavedDocumentDraft(prev, d));
    setDocumentModalOpen(false);
  };

  const handleRemoveDocument = async (d: DocumentDraft) => {
    if (!scenarioId || !d.id) return;
    if (!window.confirm(`Remove document "${d.name}"?`)) return;
    try {
      await deleteDocument(scenarioId, d.id, getToken());
      setDocuments((prev) => prev.filter((x) => x.id !== d.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove document.");
    }
  };

  // ------------------------------------------------------------------

  if (bootstrapping || loadingExisting) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
        <div className="max-w-5xl w-full mx-auto px-8 py-10">
          <p className="text-slate-500 font-label text-xs uppercase tracking-widest animate-pulse">
            {bootstrapping ? "Creating draft…" : "Loading draft…"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-5xl w-full mx-auto px-8 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-1">
              {scenarioIdProp ? "Edit Scenario" : "New Scenario"}
            </h2>
            <p className="text-slate-500 font-label text-xs uppercase tracking-widest">
              {scenarioStatus === "published"
                ? "Published scenario · changes go live when you publish"
                : "Draft scenario · saved as you publish"}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <label className="px-4 py-2 bg-surface-container-lowest text-on-surface rounded font-label text-xs uppercase tracking-widest border border-outline-variant/40 hover:bg-surface-container cursor-pointer">
              {importingArchive ? "Importing…" : "Import"}
              <input
                type="file"
                accept=".zip,application/zip"
                onChange={handleImportArchive}
                disabled={importingArchive || savingDraft || publishing}
                className="hidden"
              />
            </label>
            <button
              type="button"
              onClick={handleExportArchive}
              disabled={exportingArchive || importingArchive || savingDraft || publishing}
              className="px-4 py-2 bg-surface-container-lowest text-on-surface rounded font-label text-xs uppercase tracking-widest border border-outline-variant/40 hover:bg-surface-container disabled:opacity-50"
            >
              {exportingArchive ? "Exporting…" : "Export"}
            </button>
            <button
              type="button"
              onClick={handleDiscardDraft}
              className="text-error font-label text-xs uppercase tracking-widest hover:underline"
            >
              Discard Draft
            </button>
            {canSaveDraft && (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className="px-4 py-2 bg-surface-container-lowest text-primary rounded font-label text-xs uppercase tracking-widest border border-primary/30 hover:bg-surface-container disabled:opacity-50"
              >
                {savingDraft ? "Saving…" : "Save Draft"}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/30 rounded-md px-4 py-3 text-error text-sm mb-6">
            {error}
          </div>
        )}

        {/* Basics card */}
        <section className="admin-card">
          <h3 className="admin-card-title">
            Basics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="form-label">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="form-control"
              />
            </div>
            <div>
              <label className="form-label">
                Category
              </label>
              <CategoryCombobox
                categories={categories}
                value={categoryValue}
                onChange={setCategoryValue}
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="form-control"
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Player role</label>
              <p className="text-xs text-slate-500 mb-1.5">
                Who the player is, in second person. Shown in the briefing
                rail.
              </p>
              <textarea
                value={playerRole}
                onChange={(e) => setPlayerRole(e.target.value)}
                rows={2}
                className="form-control"
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Objective</label>
              <p className="text-xs text-slate-500 mb-1.5">
                What success looks like, in the player's terms.
              </p>
              <textarea
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                rows={2}
                className="form-control"
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Briefing</label>
              <p className="text-xs text-slate-500 mb-1.5">
                The situation, as the player meets it. Markdown. Longer and
                richer than the card summary above.
              </p>
              <textarea
                value={briefing}
                onChange={(e) => setBriefing(e.target.value)}
                rows={8}
                className="form-control"
              />
            </div>
            <div>
              <label className="form-label">
                Logo
              </label>
              <p className="text-xs text-slate-500 mb-1.5">
                Square mark, shown on cards and in the chat scenario pane.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded bg-surface-container border border-outline-variant/30 overflow-hidden flex items-center justify-center">
                  {logoPreviewUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={logoPreviewUrl}
                      alt="Logo preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="material-symbols-outlined text-slate-500">
                      image
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoSelect}
                  className="form-file-input"
                />
                {logoUploading && (
                  <span className="text-xs text-slate-500 animate-pulse">
                    Uploading…
                  </span>
                )}
              </div>
              <p className="form-help">Max 10 MB.</p>
            </div>
            <div>
              <label className="form-label">
                Hero image
              </label>
              <p className="text-xs text-slate-500 mb-1.5">
                Wide key art, shown across the top of the briefing page and
                the library card. Roughly 2400×800 works best — it is
                centre-cropped, so keep important detail near the middle.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-32 h-20 rounded bg-surface-container border border-outline-variant/30 overflow-hidden flex items-center justify-center">
                  {heroPreviewUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={heroPreviewUrl}
                      alt="Hero image preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="material-symbols-outlined text-slate-500">
                      image
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHeroSelect}
                  className="form-file-input"
                />
                {heroUploading && (
                  <span className="text-xs text-slate-500 animate-pulse">
                    Uploading…
                  </span>
                )}
              </div>
              <p className="form-help">Max 10 MB.</p>
            </div>
          </div>
        </section>

        {/* Instructions card */}
        <section className="admin-card">
          <h3 className="admin-card-title">
            Context Prompt
          </h3>
          <p className="text-slate-500 text-xs font-body mb-3">
            Markdown background injected into every persona's prompt at chat
            time.
          </p>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={14}
            className="form-control form-control--mono"
          />
        </section>

        {/* Personas card */}
        <section className="admin-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="admin-card-title admin-card-title--inline">
              Personas
            </h3>
            <button
              type="button"
              onClick={handleAddPersona}
              className="button-subtle"
            >
              + Add Persona
            </button>
          </div>
          {personas.length === 0 ? (
            <p className="text-slate-500 italic text-sm">
              No personas yet. Add at least one before publishing.
            </p>
          ) : (
            <ul className="space-y-2">
              {personas.map((p) => (
                <li
                  key={p.id ?? p.name}
                  className="admin-list-item"
                >
                  <div className="flex items-center gap-3">
                    <PersonaAvatar
                      name={p.name}
                      src={p.avatarPreviewUrl}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className="font-body text-sm text-on-surface">
                        {p.name}
                      </p>
                      <p className="font-body text-xs text-slate-500">
                        {p.role}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEditPersona(p)}
                      className="icon-action"
                      aria-label="Edit"
                    >
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemovePersona(p)}
                      className="icon-action icon-action--danger"
                      aria-label="Remove"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Documents card */}
        <section className="admin-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="admin-card-title admin-card-title--inline">
              Documents
            </h3>
            <button
              type="button"
              onClick={handleAddDocument}
              className="button-subtle"
            >
              + Add Document
            </button>
          </div>
          {documents.length === 0 ? (
            <p className="text-slate-500 italic text-sm">
              No documents yet. Personas can reveal these during chat.
            </p>
          ) : (
            <ul className="space-y-2">
              {documents.map((d) => (
                <li
                  key={d.id ?? d.name}
                  className="admin-list-item"
                >
                  <div>
                    <p className="font-body text-sm text-on-surface">{d.name}</p>
                    <p className="font-body text-xs text-slate-500 line-clamp-1">
                      {d.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEditDocument(d)}
                      className="icon-action"
                      aria-label="Edit"
                    >
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(d)}
                      className="icon-action icon-action--danger"
                      aria-label="Remove"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Footer */}
        <div className="admin-card admin-card--flush flex items-center justify-between">
          <div>
            {validationErrors.length > 0 ? (
              <ul className="text-xs text-slate-500 list-disc pl-4">
                {validationErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500">
                Ready to publish. The scenario will appear in the library for
                all users.
              </p>
            )}
          </div>
          <div className="flex flex-col items-end">
            {isAdmin && (
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  disabled={publishing}
                  className="h-4 w-4 rounded border-outline-variant/40 bg-surface-container-low"
                />
                <span className="text-xs font-label uppercase tracking-wider text-slate-400">
                  Shared catalog — visible to every organization
                </span>
              </label>
            )}
            <button
              type="button"
              onClick={handlePublish}
              disabled={publishing || validationErrors.length > 0}
              className="button-primary button-primary--large"
            >
              {publishing ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
      </div>

      {/* Persona modal */}
      {scenarioId && (
        <PersonaFormModal
          open={personaModalOpen}
          persona={editingPersona}
          accessibleDocumentOptions={documents
            .filter((d) => !!d.id)
            .map((d) => ({ id: d.id as string, name: d.name }))}
          scenarioId={scenarioId}
          getToken={getToken}
          onSave={handlePersonaSaved}
          onClose={() => setPersonaModalOpen(false)}
          createBlobUrl={createBlobUrl}
          revokeBlobUrl={revokeBlobUrl}
        />
      )}

      {/* Document modal */}
      {scenarioId && (
        <DocumentFormModal
          open={documentModalOpen}
          document={editingDocument}
          scenarioId={scenarioId}
          getToken={getToken}
          onSave={handleDocumentSaved}
          onClose={() => setDocumentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ScenarioCreatePage;
